import { bindPrototypeMethodsToSelf } from '@wix/panorama-common/utils';
import type {
  Reporter,
  BaseParams,
  EventPayload,
} from '@wix/panorama-common-shared/types';
import {
  createGlobalConfig,
  type GlobalConfig,
} from '@wix/panorama-common/global-config';
import { defaults } from './utils/general-utils';
import { type PanoramaClient, PanoramaClientImpl } from './panorama-client';
import type { PluginArgs, PluginFunction } from './plugins';
import type { ClientOptions, FactoryOptions } from './types';
import { HooksManager } from './plugins/hooks-manager';
import { BasePanoramaClient } from './panorama-client-base';
import { Scope } from './lib/scope';

export interface PanoramaClientFactory<Payload extends unknown = EventPayload> {
  withGlobalConfig(
    globalConfig: GlobalConfig<Payload>,
  ): PanoramaClientFactory<Payload>;

  withReporter(reporter: Reporter<Payload>): PanoramaClientFactory<Payload>;

  setMuted(isMuted: boolean): PanoramaClientFactory<Payload>;

  use(
    plugin: (args: PluginArgs<Payload>) => void,
  ): PanoramaClientFactory<Payload>;

  client(options?: ClientOptions): PanoramaClient;
}

class PanoramaClientFactoryImpl<Payload extends unknown = EventPayload>
  implements PanoramaClientFactory<Payload>
{
  private globalConfig?: GlobalConfig<Payload>;
  private reporter: Reporter<Payload> = () => true;
  private plugins: PluginFunction<Payload>[] = [];
  private isMuted: boolean = false;

  constructor(private readonly options: FactoryOptions) {
    bindPrototypeMethodsToSelf(this);
  }

  withGlobalConfig(globalConfig: GlobalConfig<Payload>) {
    this.globalConfig = globalConfig;

    return this;
  }

  withReporter(reporter: Reporter<Payload>) {
    this.reporter = reporter;

    return this;
  }

  setMuted(isMuted: boolean) {
    this.isMuted = isMuted;

    return this;
  }

  use(plugin: (args: PluginArgs<Payload>) => void) {
    this.plugins.push(plugin);

    return this;
  }

  client(options: ClientOptions = {}) {
    const { pluginParams } = options;

    const globalConfig = this.globalConfig ?? createGlobalConfig();
    const reporter = this.reporter as any;
    const hooksManager = new HooksManager();
    const baseParams = defaults(
      options.baseParams,
      this.options.baseParams,
    ) as Required<BaseParams>;
    const data = new Scope(this.options.data).addDataScope(options.data);

    const pluginClientParams = pluginParams ?? {};

    const baseClient = new BasePanoramaClient({
      baseParams,
      globalConfig: globalConfig as unknown as GlobalConfig,
      isMuted: () => this.isMuted,
      reporter,
      hooksManager,
      data,
    });

    const client = new PanoramaClientImpl({
      baseClient,
      hooksManager,
    });

    this.plugins.forEach((plugin) =>
      plugin({
        hooksManager,
        globalConfig,
        reporter,
        baseClient,
        baseParams,
        pluginParams: pluginClientParams,
      }),
    );

    return client;
  }
}

export const panoramaClient = <Payload extends unknown = EventPayload>(
  options: FactoryOptions = {},
): PanoramaClientFactory<Payload> =>
  new PanoramaClientFactoryImpl<Payload>(options);
